import { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  NavLink,
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";

import { supabase } from "./lib/supabase";
import { columnsToTravelDates, travelDatesToColumns, ageFromDob, rowToUser } from "./lib/profile";
import { signPhotos, toStoredPhoto, withSignedPhotos } from "./lib/photos";
import { Icon } from "./components/Icon";
import { Wordmark } from "./components/Wordmark";
// Screens are split per route: a returning user never downloads the onboarding
// flow, and the first paint only needs whichever screen the URL asks for.
const AuthScreen    = lazy(() => import("./screens/AuthScreen").then(m => ({ default: m.AuthScreen })));
const Onboarding    = lazy(() => import("./screens/Onboarding").then(m => ({ default: m.Onboarding })));
const SwipeScreen   = lazy(() => import("./screens/SwipeScreen").then(m => ({ default: m.SwipeScreen })));
const MatchesScreen = lazy(() => import("./screens/MatchesScreen").then(m => ({ default: m.MatchesScreen })));
const ChatScreen    = lazy(() => import("./screens/ChatScreen").then(m => ({ default: m.ChatScreen })));
const ProfileScreen = lazy(() => import("./screens/ProfileScreen").then(m => ({ default: m.ProfileScreen })));
const PrivacyPolicy  = lazy(() => import("./legal/PrivacyPolicy").then(m => ({ default: m.PrivacyPolicy })));
const TermsOfService = lazy(() => import("./legal/TermsOfService").then(m => ({ default: m.TermsOfService })));
const CookiePolicy   = lazy(() => import("./legal/CookiePolicy").then(m => ({ default: m.CookiePolicy })));

const NAV = [
  { to: "/discover", icon: "compass", label: "Discover" },
  { to: "/matches",  icon: "users",   label: "Matches" },
  { to: "/profile",  icon: "user",    label: "Profile" },
];

function Splash({ message = "Finding your people…" }) {
  return (
    <div className="app-shell" style={{ alignItems: "center", justifyContent: "center" }}>
      <Wordmark style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 68,
        fontWeight: 700, color: "var(--ink)", letterSpacing: -2.5, lineHeight: 1 }} />
      <div style={{ fontSize: 15, color: "var(--ink-light)", marginTop: 14 }}>{message}</div>
    </div>
  );
}

// Resolves :matchId from the URL against the loaded matches, so a chat can be
// linked to directly — from a notification, an email, or a refreshed tab.
function ChatRoute({ matches, matchesLoading, authUser, onRead, onLeave }) {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const match = matches.find(m => m.matchId === matchId);

  if (!match) {
    if (matchesLoading) return <Splash message="Opening your chat…" />;
    return <Navigate to="/matches" replace />;
  }

  return (
    <ChatScreen
      user={match.user}
      matchId={matchId}
      authUser={authUser}
      onBack={() => { navigate("/matches"); onLeave(); }}
      onBlocked={() => { navigate("/matches", { replace: true }); onLeave(); }}
      onRead={onRead}
    />
  );
}

export default function App() {
  const [appState, setAppState] = useState("loading"); // loading | auth | onboard | main
  const [authUser, setAuthUser] = useState(null);
  const [userPrefs, setUserPrefs] = useState(null);
  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(true);
  const [matchesFailed, setMatchesFailed] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [notifOpen, setNotifOpen] = useState(false);

  // onAuthStateChange also fires on token refresh and on tab focus. Without
  // this guard the profile reloaded on every one of those, resetting state
  // underneath the user mid-session.
  const profileLoadedFor = useRef(null);

  const checkProfile = useCallback(async (userId) => {
    // date_of_birth is not client-readable on profiles (migration 0011), not
    // even on your own row; get_my_date_of_birth() returns only the caller's.
    const [{ data }, { data: dateOfBirth }] = await Promise.all([
      supabase
        .from("profiles")
        .select(
          "name, age, from_location, regions, activities, budget, vibe, " +
          "travel_speed, accommodation, duration, travel_from, travel_to, dates_flexible, " +
          "avatar_url, bio, trips_count, extra_photos, gender, languages, is_verified, age_verified"
        )
        .eq("id", userId)
        .maybeSingle(),
      supabase.rpc("get_my_date_of_birth"),
    ]);

    const complete = data && data.regions?.length > 0 && data.activities?.length > 0 && data.budget;

    if (complete) {
      const [mainPhoto, ...extraPhotos] = await signPhotos([data.avatar_url, ...(data.extra_photos || [])]);
      setUserPrefs({
        name:          data.name,
        age:           data.age,
        dateOfBirth:   dateOfBirth || "",
        from:          data.from_location,
        bio:           data.bio,
        regions:       data.regions || [],
        activities:    data.activities || [],
        budget:        data.budget,
        duration:      data.duration,
        vibe:          data.vibe,
        travelSpeed:   data.travel_speed,
        accommodation: data.accommodation,
        gender:        data.gender || "",
        languages:     data.languages || [],
        tripsCount:    data.trips_count || 0,
        verified:      data.is_verified,
        ageVerified:   data.age_verified,
        travelDates:   columnsToTravelDates(data),
        mainPhoto:     mainPhoto || null,
        extraPhotos:   extraPhotos.filter(Boolean),
      });
      setAppState("main");
    } else {
      setAppState("onboard");
    }
  }, []);

  useEffect(() => {
    let active = true;

    const handleSession = (session) => {
      if (!active) return;

      if (session?.user) {
        // Only swap the object when the identity actually changes. A token
        // refresh hands back an equivalent-but-new object, and anything
        // depending on it would otherwise re-run and reset the screen.
        setAuthUser(prev => (prev?.id === session.user.id ? prev : session.user));

        if (profileLoadedFor.current !== session.user.id) {
          profileLoadedFor.current = session.user.id;
          checkProfile(session.user.id);
        }
      } else {
        profileLoadedFor.current = null;
        setAuthUser(null);
        setUserPrefs(null);
        setMatches([]);
        setAppState("auth");
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => handleSession(session));

    const { data: { subscription } } =
      supabase.auth.onAuthStateChange((_event, session) => handleSession(session));

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [checkProfile]);

  // One call returns every match, the other person's profile, the last message
  // and the unread count. This replaces the matches loader and the notification
  // poller, which between them made (2 x matches) + 1 requests every 30s.
  const loadMatches = useCallback(async () => {
    if (!authUser?.id) return;

    const { data, error } = await supabase.rpc("get_matches_overview");
    if (error) {
      console.error("get_matches_overview failed", error);
      // Only surface it when there is nothing to show. A failed background
      // refresh keeps the list it already has.
      setMatchesFailed(true);
      setMatchesLoading(false);
      return;
    }

    const rows = await withSignedPhotos(data);
    setMatchesFailed(false);
    setMatches(rows.map(row => ({
      matchId:       row.match_id,
      matchedAt:     row.matched_at,
      tripConfirmed: row.trip_confirmed,
      youConfirmed:  row.you_confirmed,
      theyConfirmed: row.they_confirmed,
      user:          rowToUser(row),
      lastMessage:   row.last_message,
      lastMessageAt: row.last_message_at,
      lastSenderId:  row.last_sender_id,
      unreadCount:   row.unread_count || 0,
    })));
    setMatchesLoading(false);
  }, [authUser?.id]);

  useEffect(() => {
    if (appState !== "main" || !authUser) return;

    loadMatches();
    const interval = setInterval(loadMatches, 30000);
    const onFocus = () => loadMatches();
    window.addEventListener("focus", onFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [appState, authUser, loadMatches]);

  // Notifications are a view over the same data, not a second set of queries.
  const notifications = matches
    .filter(m => m.unreadCount > 0 || !m.lastMessage)
    .map(m => m.unreadCount > 0
      ? {
          id: `msg-${m.matchId}`, type: "message", matchId: m.matchId, user: m.user,
          text: `${m.user.name}: ${m.lastMessage}`,
          count: m.unreadCount, time: new Date(m.lastMessageAt),
        }
      : {
          id: `match-${m.matchId}`, type: "match", matchId: m.matchId, user: m.user,
          text: `You matched with ${m.user.name}!`,
          count: 1, time: new Date(m.matchedAt),
        })
    .sort((a, b) => b.time - a.time);

  // Uploads any newly picked photos and returns their stored paths (see
  // lib/photos.js — the bucket is private, so there is no public URL). Extra photos
  // get a unique filename rather than extra_<index>, which used to overwrite a
  // different photo whenever one was removed and another added.
  const uploadPhotos = async (prefs) => {
    let avatarUrl = prefs.mainPhoto || null;
    // A photo already uploaded arrives as the signed URL it was shown with; it
    // is saved in its stored form, never as that expiring link.
    if (avatarUrl && !avatarUrl.startsWith("blob:")) avatarUrl = toStoredPhoto(avatarUrl);

    // A newly added photo moved into the main slot arrives as a blob: URL with
    // no mainPhotoFile. Without this it was saved to avatar_url verbatim — a
    // URL that only ever resolved in the uploader's own browser tab.
    let mainFile = prefs.mainPhotoFile;
    if (!mainFile && avatarUrl?.startsWith("blob:")) {
      const blob = await (await fetch(avatarUrl)).blob();
      mainFile = new File([blob], `avatar.${blob.type.split("/")[1] || "jpg"}`, { type: blob.type });
    }

    if (mainFile) {
      const ext = mainFile.name.split(".").pop();
      const path = `${authUser.id}/avatar.${ext}`;
      const { error } = await supabase.storage
        .from("avatars")
        .upload(path, mainFile, { upsert: true });
      if (error) throw new Error("Photo upload failed.");
      // Same path on every upload (upsert), so version the URL — otherwise
      // browsers and the CDN keep serving the photo it replaced.
      avatarUrl = `${path}?v=${Date.now()}`;
    }

    const extraUrls = [];
    for (const entry of prefs.extraPhotos || []) {
      if (!entry.startsWith("blob:")) {
        const stored = toStoredPhoto(entry);
        if (stored) extraUrls.push(stored);
        continue;
      }
      const blob = await (await fetch(entry)).blob();
      const ext = blob.type.split("/")[1] || "jpg";
      const path = `${authUser.id}/extra_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error } = await supabase.storage
        .from("avatars")
        .upload(path, blob, { contentType: blob.type });
      if (!error) extraUrls.push(path);
    }

    return { avatarUrl, extraUrls };
  };

  // Columns shared by onboarding and profile edits.
  const profileColumns = (prefs, avatarUrl, extraUrls) => ({
    name:          prefs.name,
    date_of_birth: prefs.dateOfBirth || null,
    age:           prefs.dateOfBirth ? ageFromDob(prefs.dateOfBirth) : parseInt(prefs.age, 10),
    from_location: prefs.from,
    bio:           prefs.bio,
    regions:       prefs.regions,
    activities:    prefs.activities,
    accommodation: prefs.accommodation,
    travel_speed:  prefs.travelSpeed,
    budget:        prefs.budget,
    duration:      prefs.duration,
    vibe:          prefs.vibe,
    gender:        prefs.gender,
    languages:     prefs.languages,
    // Written to both the legacy JSON column and the real date columns the
    // deck query filters on.
    travel_dates:  JSON.stringify(prefs.travelDates),
    ...travelDatesToColumns(prefs.travelDates),
    avatar_url:    avatarUrl,
    extra_photos:  extraUrls,
  });

  const handleOnboardComplete = async (prefs) => {
    setSaveError("");

    let avatarUrl, extraUrls;
    try {
      ({ avatarUrl, extraUrls } = await uploadPhotos(prefs));
    } catch (err) {
      setSaveError(err.message);
      return;
    }

    // Update the placeholder row handle_new_user made at signup, and insert
    // only if it is missing: an update against a missing row affects zero
    // rows and returns no error, which once silently dropped the whole
    // profile. Not .upsert(): its ON CONFLICT DO UPDATE reads EXCLUDED
    // columns, which needs SELECT on date_of_birth, and clients don't have
    // that (migration 0011).
    const columns = profileColumns(prefs, avatarUrl, extraUrls);
    let { data, error } = await supabase
      .from("profiles")
      .update(columns)
      .eq("id", authUser.id)
      .select("id")
      .maybeSingle();
    if (!error && !data) {
      ({ data, error } = await supabase
        .from("profiles")
        .insert({ id: authUser.id, ...columns })
        .select("id")
        .maybeSingle());
    }

    if (error || !data) {
      // Log the real reason. A generic message here once hid an RLS rejection
      // for days, because nothing recorded what the database actually said.
      console.error("profile save failed", error);
      setSaveError(
        error?.message?.includes("18 or older")
          ? "You must be 18 or older to use Duami."
          : `Couldn't save your profile. ${error?.message ?? "Please try again."}`
      );
      return;
    }

    const [mainPhoto, ...extraPhotos] = await signPhotos([avatarUrl, ...extraUrls]);
    setUserPrefs({ ...prefs, mainPhoto, extraPhotos: extraPhotos.filter(Boolean) });
    setAppState("main");
  };

  const handleProfileUpdate = async (updatedPrefs) => {
    setSaveError("");

    let avatarUrl, extraUrls;
    try {
      ({ avatarUrl, extraUrls } = await uploadPhotos(updatedPrefs));
    } catch (err) {
      setSaveError(err.message);
      return false;
    }

    const { error } = await supabase
      .from("profiles")
      .update(profileColumns(updatedPrefs, avatarUrl, extraUrls))
      .eq("id", authUser.id);

    if (error) {
      console.error("profile update failed", error);
      setSaveError(`Couldn't save your changes. ${error.message}`);
      return false;
    }

    const [mainPhoto, ...extraPhotos] = await signPhotos([avatarUrl, ...extraUrls]);
    setUserPrefs({ ...updatedPrefs, mainPhoto, extraPhotos: extraPhotos.filter(Boolean) });
    return true;
  };

  const handleSignOut = () => supabase.auth.signOut();

  // A thread was just read — clear its badge locally rather than refetching.
  const handleThreadRead = (matchId) =>
    setMatches(ms => ms.map(m => (m.matchId === matchId ? { ...m, unreadCount: 0 } : m)));

  return (
    <BrowserRouter>
      <AppRoutes
        appState={appState}
        authUser={authUser}
        userPrefs={userPrefs}
        matches={matches}
        matchesLoading={matchesLoading}
        matchesFailed={matchesFailed}
        notifications={notifications}
        notifOpen={notifOpen}
        setNotifOpen={setNotifOpen}
        saveError={saveError}
        onMatch={loadMatches}
        onRefreshMatches={loadMatches}
        onThreadRead={handleThreadRead}
        onOnboardComplete={handleOnboardComplete}
        onProfileUpdate={handleProfileUpdate}
        onSignOut={handleSignOut}
      />
    </BrowserRouter>
  );
}

function AppRoutes({
  appState, authUser, userPrefs, matches, matchesLoading, matchesFailed,
  notifications, notifOpen, setNotifOpen, saveError,
  onMatch, onRefreshMatches, onThreadRead,
  onOnboardComplete, onProfileUpdate, onSignOut,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const openChat = useCallback((_user, matchId) => {
    setNotifOpen(false);
    navigate(`/matches/${matchId}`);
  }, [navigate, setNotifOpen]);

  // Signed-in routes redirect out when the session isn't ready for them.
  const guard = (element) => {
    if (appState === "loading") return <Splash />;
    if (appState === "auth") return <Navigate to="/auth" replace />;
    if (appState === "onboard") return <Navigate to="/onboarding" replace />;
    return element;
  };

  // Signed-out routes redirect in once the session is ready.
  const publicOnly = (element, allowedState) => {
    if (appState === "loading") return <Splash />;
    if (appState === "main") return <Navigate to="/discover" replace />;
    if (appState !== allowedState) {
      return <Navigate to={appState === "auth" ? "/auth" : "/onboarding"} replace />;
    }
    return element;
  };

  const unreadTotal = matches.reduce((n, m) => n + (m.unreadCount || 0), 0);
  const inChat = /^\/matches\/[^/]+$/.test(location.pathname);
  const inLegal = /^\/(privacy|terms|cookies)$/.test(location.pathname);
  const showNav = appState === "main" && !inChat && !inLegal;

  return (
    <div className="app-shell">
      <Suspense fallback={<Splash />}>
      <Routes>
        <Route path="/auth" element={publicOnly(<AuthScreen />, "auth")} />

        <Route path="/onboarding" element={publicOnly(
          <>
            <Onboarding onComplete={onOnboardComplete} />
            {saveError && (
              <div style={{ position: "fixed", bottom: 100, left: 20, right: 20,
                background: "var(--coral)", color: "white", borderRadius: 12,
                padding: "12px 16px", fontSize: 13, fontWeight: 500, textAlign: "center", zIndex: 200 }}>
                <span className="inline-icon"><Icon name="alert" size={13} color="currentColor" /></span>{saveError}
              </div>
            )}
          </>,
          "onboard"
        )} />

        <Route path="/discover" element={guard(
          <SwipeScreen
            userPrefs={userPrefs}
            onMatch={onMatch}
            authUser={authUser}
            notifications={notifications}
            notifOpen={notifOpen}
            setNotifOpen={setNotifOpen}
            onOpenChat={openChat}
            onNavigate={(n) => {
              if (n.type === "message") openChat(n.user, n.matchId);
              else { setNotifOpen(false); navigate("/matches"); }
            }}
          />
        )} />

        <Route path="/matches" element={guard(
          <MatchesScreen matches={matches} userId={authUser?.id} loading={matchesLoading} failed={matchesFailed}
            onRetry={onRefreshMatches} onOpenChat={openChat} />
        )} />

        <Route path="/matches/:matchId" element={guard(
          <ChatRoute
            matches={matches}
            matchesLoading={matchesLoading}
            authUser={authUser}
            onRead={onThreadRead}
            onLeave={onRefreshMatches}
          />
        )} />

        <Route path="/profile" element={guard(
          <ProfileScreen
            userPrefs={userPrefs}
            userId={authUser?.id}
            matchCount={matches.length}
            onSignOut={onSignOut}
            onUpdate={onProfileUpdate}
            saveError={saveError}
          />
        )} />

        {/* Unguarded: readable signed in or out, and before agreeing to them. */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms"   element={<TermsOfService />} />
        <Route path="/cookies" element={<CookiePolicy />} />

        <Route path="*" element={<Navigate to="/discover" replace />} />
      </Routes>
      </Suspense>

      {showNav && (
        <nav className="nav-bar">
          {NAV.map(n => (
            <NavLink key={n.to} to={n.to}
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
              <div className="nav-icon-wrap">
                <Icon name={n.icon} size={22} />
                {n.to === "/matches" && unreadTotal > 0 && (
                  <div className="nav-badge">{unreadTotal}</div>
                )}
              </div>
              <span className="nav-label">{n.label}</span>
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
}
